import React, { useEffect, useState } from 'react'
import Header, { SwitchViewType } from '../common/Header'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'
import { selectTeam } from 'state'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'particles/themes/styled'
import styles from '../Teams/Roles/styles'
import { Formik } from 'formik'
import Text from 'particles/Text'
import { FormikTextField } from 'particles/TextInputs/TextField'
import ImageUploaderField from 'components/Shared/Fields/ImageUploadField'
import { UploadType } from 'services/api/uploads'
import RectangleButton from 'particles/Button/RectangleButton'
import * as yup from 'yup'
import { COUNTRY_ENUM } from 'services/api/constants'
import {
    BaseBusinessDetails,
    TeamActions,
    getBusinessDetails,
    getTeam,
    selectBusinessDetail,
    updateBusinessDetails,
} from 'state/teams'
import qs from 'query-string'
import ContactInformation from './ContactInformation'
import CountryDropdown from './CountryDropdown'

const requiredSomeFieldsByCountry: Record<string, COUNTRY_ENUM[]> = {
    tax_id: [COUNTRY_ENUM.TH, COUNTRY_ENUM.TW, COUNTRY_ENUM.VN, COUNTRY_ENUM.ID],
    tax_id_address: [COUNTRY_ENUM.ID],
}

const BusinessDetails = ({ className }: any) => {
    const [t] = useTranslation()
    const { uuid } = useParams<{ uuid: string }>()
    const history = useHistory()
    const team = useSelector(selectTeam(uuid as string))
    const businessDetails = useSelector(selectBusinessDetail)
    const dispatch = useDispatch()
    const [showError, setShowError] = useState(false)
    const params = qs.parse(location.search)
    const redirectTo = params.redirectTo

    const maxAddressErr = t('errorMessage.streetAddressWordLimit', { maximumInputLimit: 250 })
    const maxAddress2Err = t('errorMessage.streetAddressWordLimit', { maximumInputLimit: 150 })
    const formatLinkError = t('campaign.template.downloadAppFrom.errorMessage.wrongFormate')

    useEffect(() => {
        if (typeof uuid === 'string') {
            dispatch(getBusinessDetails({ teamUuid: uuid }))
            dispatch(getTeam({ teamUuid: uuid }))
        }

        return (): void => {
            dispatch({ type: TeamActions.CLEAR_TEAM_LIST })
        }
    }, [uuid])

    const handleSwitchViewType = (type: SwitchViewType) => {
        switch (type) {
            case SwitchViewType.MEMBERS:
                history.push(`/team/${uuid}/members`)
                break
            case SwitchViewType.ROLE_MANAGEMENT:
                history.push(`/teams/${uuid}/roles`)
                break
            case SwitchViewType.BRAND_ACCESS:
                history.push(`/team/${uuid}/brands`)
                break
            case SwitchViewType.BUSINESS_DETAILS:
                history.push(`/teams/${uuid}/business-details`)
                break
        }
    }

    const initialValues: BaseBusinessDetails = businessDetails ?? {
        business_name: '',
        country: null,
        address_1: '',
        address_2: '',
        city: '',
        state: '',
        postal: '',
        address_country: null,
        tax_id: '',
        tax_id_address: '',
        business_link: '',
        supporting_documents: [],
        contact_information: [
            {
                contact: '',
                email: '',
                full_name: '',
                job_title: '',
            },
        ],
    }

    const validationSchema = yup.object({
        business_name: yup.string().nullable().required(t('team.billingInformation.businessName.emptyError')),
        country: yup.string().nullable().required(t('team.billingInformation.country.emptyError')),
        address_1: yup
            .string()
            .nullable()
            .required(t('team.billingInformation.streetAddress.emptyError'))
            .max(255, maxAddressErr),
        address_2: yup.string().nullable().notRequired().max(150, maxAddress2Err),
        city: yup.string().nullable().required(t('team.billingInformation.townCity.emptyError')),
        state: yup.string().nullable().required(t('team.billingInformation.stateProvineRegion.emptyError')),
        postal: yup.string().nullable().required(t('accountDetails.address.postalCode.emptyError')),
        address_country: yup.string().nullable().required(t('accountDetails.personal.countryHeader.emptyError')),

        // conditional by country
        tax_id: yup
            .string()
            .nullable()
            .when('country', {
                is: country => requiredSomeFieldsByCountry.tax_id?.includes(country),
                then: yup.string().nullable().required(t('team.billingInformation.taxIDAddress.emptyError')),
            }),
        tax_id_address: yup
            .string()
            .nullable()
            .when('country', {
                is: country => requiredSomeFieldsByCountry.tax_id_address?.includes(country),
                then: yup.string().nullable().required(t('team.billingInformation.taxIDAddress.emptyError')),
            }),

        business_link: yup
            .string()
            .nullable()
            .test({
                message: () => formatLinkError,
                test: data => {
                    const linkPattern = /((https|http)?:\/\/[^\s]+\.[\w]{2,3})/g
                    if (data && !linkPattern.test(data)) {
                        return false
                    }

                    return true
                },
            })
            .required(t('team.billingInformation.businessDigitalPresence.emptyError')),
        contact_information: yup.array(
            yup.object({
                full_name: yup.string().nullable().required(t('accountDetails.personal.nameHeader.emptyErrorder')),
                job_title: yup
                    .string()
                    .nullable()
                    .required(t('team.billingInformation.businessManager.jobTitle.emptyError')),
                email: yup
                    .string()
                    .email(t('errorMessage.validation.enterValidEmail', { email: 'name@example.com' }))
                    .required(t('errorMessage.validation.enterEmailAddress')),
                contact: yup
                    .string()
                    .nullable()
                    .required(t('team.billingInformation.businessManager.phone.emptyError')),
            }),
        ),
    })

    return (
        <div className={className} data-testid="test-business-details-container">
            <Header
                isNotEdited={false}
                handleLeavePageWithoutSave={handleSwitchViewType}
                switchProps={{
                    currentViewType: 'businessDetails',
                }}
                hideTeamTooltip={team?.is_internal}
            />

            <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={values => {
                    dispatch(
                        updateBusinessDetails({
                            teamUuid: uuid as string,
                            ...values,
                        }),
                    )
                    if (typeof redirectTo === 'string') {
                        history.replace(redirectTo)
                    }
                }}
            >
                {formikBag => (
                    <>
                        <div className="business-details-wrapper">
                            <div className="form-container">
                                <Text scale="H3">{t('Billing Information')}</Text>
                                <div className="form-fields">
                                    <FormikTextField
                                        name="business_name"
                                        isRequired
                                        label={t('team.billingInformation.businessName.title')}
                                        dataTestId="test-input-business-name"
                                        showError={showError}
                                    />

                                    <CountryDropdown
                                        name="country"
                                        label={t('team.billingInformation.country.title')}
                                        subtext={t('team.billingInformation.country.subtext')}
                                        isReadOnly={!!businessDetails?.country}
                                    />

                                    <FormikTextField
                                        name="address_1"
                                        isRequired
                                        label={t('team.billingInformation.streetAddress.title')}
                                        dataTestId="test-input-street-adress-1"
                                        maxInputLength={250}
                                        showInputLength
                                        showError={showError || formikBag.errors.address_1 === maxAddressErr}
                                        validateByTouch={false}
                                    />
                                    <FormikTextField
                                        name="address_2"
                                        label={t('team.billingInformation.streetAddress2.title')}
                                        maxInputLength={150}
                                        showInputLength
                                        showError={showError || formikBag.errors.address_2 === maxAddress2Err}
                                        validateByTouch={false}
                                    />

                                    <div className="flex-row">
                                        <FormikTextField
                                            name="city"
                                            isRequired
                                            label={t('team.billingInformation.townCity.title')}
                                            dataTestId="test-input-business-town"
                                            showError={showError}
                                        />
                                        <FormikTextField
                                            name="state"
                                            label={t('team.billingInformation.stateProvineRegion.title')}
                                            dataTestId="test-input-business-state"
                                            showError={showError}
                                            isRequired
                                        />
                                    </div>

                                    <div className="flex-row">
                                        <FormikTextField
                                            name="postal"
                                            isRequired
                                            label={t('accountDetails.address.postalCode')}
                                            dataTestId="test-input-business-postcode"
                                            showError={showError}
                                        />

                                        <CountryDropdown
                                            name="address_country"
                                            label={t('team.billingInformation.country.title')}
                                        />
                                    </div>

                                    <FormikTextField
                                        name="tax_id"
                                        isRequired={
                                            !!formikBag.values.country &&
                                            requiredSomeFieldsByCountry.tax_id?.includes(formikBag.values.country)
                                        }
                                        label={t('team.billingInformation.taxID.title')}
                                        dataTestId="test-input-business-tax-id"
                                        showError={showError}
                                    />
                                    <FormikTextField
                                        name="tax_id_address"
                                        isRequired={
                                            !!formikBag.values.country &&
                                            requiredSomeFieldsByCountry.tax_id_address?.includes(
                                                formikBag.values.country,
                                            )
                                        }
                                        label={t('team.billingInformation.taxIDAddress.title')}
                                        dataTestId="test-input-business-tax-id-address"
                                        showError={showError}
                                    />
                                    <FormikTextField
                                        name="business_link"
                                        isRequired
                                        label={t('team.billingInformation.businessDigitalPresence.title')}
                                        dataTestId="test-input-business-link"
                                        showError={showError || formikBag.errors.business_link === formatLinkError}
                                        validateByTouch={false}
                                    />

                                    <ImageUploaderField
                                        name="supporting_documents"
                                        maxFiles={10}
                                        labelSize="m"
                                        uploadContainerWidth="100%"
                                        maxWidth="100%"
                                        title={t('team.billingInformation.supportingDocuments.title')}
                                        presetType={UploadType.BILLING}
                                        acceptTypes={['image', 'pdf']}
                                        dataTestId="test-supporting-document-upload"
                                    />

                                    <ContactInformation showError={showError} />
                                </div>
                            </div>
                        </div>
                        <div className="submit-container">
                            <RectangleButton
                                type="submit"
                                onClick={() => {
                                    formikBag.submitForm()
                                    setShowError(true)
                                }}
                                label={t('editProfile.helper.discardChanges.button.cta1')}
                                dataTestId="test-business-details-save"
                            />
                        </div>
                    </>
                )}
            </Formik>
        </div>
    )
}

export default styled(BusinessDetails)`
    ${styles}

    .business-details-wrapper {
        background-color: white;
        border-radius: 4px;
        padding: 20px;
        box-sizing: border-box;
        margin-top: 16px;
    }

    .form-container {
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.spacing[400]};
    }

    .submit-container {
        background: ${({ theme }) => theme.colors.surface.primary};
        margin-top: 24px;
        padding: 2px 24px 2px 24px;
        height: 60px;
        align-items: center;
        display: flex;
        justify-content: flex-end;
    }

    .form-fields {
        max-width: 770px;
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.spacing[400]};
    }

    .flex-row {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: ${({ theme }) => theme.spacing[600]};
    }
`
